import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { shopsAPI, usersAPI } from '../services/api';
import { Shop, User, ShopPayment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import './Shops.css';

const fmt = (n: number) => Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Shops: React.FC = () => {
  const { isAdmin, isAgent } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState<Shop[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Shop | null>(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);

  // Shop form
  const [formData, setFormData] = useState({
    name: '', description: '', address: '', phone: '',
    isActive: true, ownerId: '', creditLimit: '', maxDebtDays: '',
  });

  // Payment modal
  const [paymentShop, setPaymentShop] = useState<Shop | null>(null);
  const [payments, setPayments] = useState<ShopPayment[]>([]);
  const [payAmount, setPayAmount] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 10 };
      if (search) params.search = search;
      if (filterActive !== '') params.isActive = filterActive === 'true';
      if (showDeleted) params.withDeleted = true;
      const res = await shopsAPI.getAll(params);
      setShops(res.data);
      setTotalPages(res.meta.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterActive, showDeleted]);

  useEffect(() => { fetchShops(); }, [fetchShops]);
  useEffect(() => {
    if (isAdmin()) usersAPI.getAll().then(setUsers).catch(console.error);
  }, [isAdmin]);

  const resetForm = () => {
    setFormData({ name: '', description: '', address: '', phone: '', isActive: true, ownerId: '', creditLimit: '', maxDebtDays: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        name: formData.name,
        description: formData.description || undefined,
        address: formData.address || undefined,
        phone: formData.phone || undefined,
        isActive: formData.isActive,
      };
      if (isAdmin() && formData.ownerId) payload.ownerId = Number(formData.ownerId);
      if (isAdmin() && formData.creditLimit !== '') payload.creditLimit = parseFloat(formData.creditLimit) || 0;
      if (isAdmin() && formData.maxDebtDays !== '') payload.maxDebtDays = parseInt(formData.maxDebtDays) || 0;

      if (editing) {
        await shopsAPI.update(editing.id, payload);
      } else {
        await shopsAPI.create(payload);
      }
      resetForm();
      fetchShops();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (shop: Shop) => {
    setEditing(shop);
    setFormData({
      name: shop.name,
      description: shop.description || '',
      address: shop.address || '',
      phone: shop.phone || '',
      isActive: shop.isActive,
      ownerId: shop.ownerId ? String(shop.ownerId) : '',
      creditLimit: String(shop.creditLimit ?? 0),
      maxDebtDays: String(shop.maxDebtDays ?? 0),
    });
    setShowForm(true);
  };

  const handleDelete = async (shop: Shop) => {
    if (!window.confirm(`Удалить магазин "${shop.name}"?`)) return;
    try {
      await shopsAPI.delete(shop.id);
      fetchShops();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleRestore = async (shop: Shop) => {
    if (!window.confirm(`Восстановить магазин "${shop.name}"?`)) return;
    try {
      await shopsAPI.restore(shop.id);
      fetchShops();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  // ── Payment modal ──────────────────────────────────────────────────────────
  const openPayments = async (shop: Shop) => {
    setPaymentShop(shop);
    setPayAmount('');
    setPayNotes('');
    setPaymentsLoading(true);
    try {
      const data = await shopsAPI.getPayments(shop.id);
      setPayments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentShop) return;
    try {
      await shopsAPI.addPayment(paymentShop.id, {
        amount: parseFloat(payAmount),
        notes: payNotes || undefined,
      });
      setPayAmount('');
      setPayNotes('');
      // Refresh payments and shop list
      const [updatedPayments, shopsRes] = await Promise.all([
        shopsAPI.getPayments(paymentShop.id),
        shopsAPI.getAll({ page, limit: 10 }),
      ]);
      setPayments(updatedPayments);
      setShops(shopsRes.data);
      // Update the active shop balance in state
      const updatedShop = shopsRes.data.find((s) => s.id === paymentShop.id);
      if (updatedShop) setPaymentShop(updatedShop);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при записи платежа');
    }
  };

  const debtColor = (shop: Shop) => {
    const debt = Number(shop.debt);
    const limit = Number(shop.creditLimit);
    if (debt === 0) return '#6b7280';
    if (limit > 0 && debt >= limit) return '#991b1b';
    if (limit > 0 && debt >= limit * 0.7) return '#92400e';
    return '#1e40af';
  };

  return (
    <div className="shops-page">
      <div className="page-header">
        <h1>Магазины</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>+ Добавить магазин</button>
      </div>

      <div className="filters-bar">
        <input
          type="text" placeholder="Поиск по названию или адресу..."
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="search-input"
        />
        <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setPage(1); }} className="filter-select">
          <option value="">Все статусы</option>
          <option value="true">Активные</option>
          <option value="false">Неактивные</option>
        </select>
        {isAdmin() && (
          <button
            className={`btn-toggle-deleted ${showDeleted ? 'active' : ''}`}
            onClick={() => { setShowDeleted(!showDeleted); setPage(1); }}
          >
            🗑 {showDeleted ? 'Скрыть удалённые' : 'Показать удалённые'}
          </button>
        )}
      </div>

      {/* ── Shop form modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Редактировать магазин' : 'Новый магазин'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="shop-form">
              <div className="form-group">
                <label>Название *</label>
                <input type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Название магазина" />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Описание магазина" rows={3} />
              </div>
              <div className="form-group">
                <label>Адрес</label>
                <input type="text" value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Адрес магазина" />
              </div>
              <div className="form-group">
                <label>Телефон</label>
                <input type="text" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+996 700 000000" />
              </div>
              {isAdmin() && (
                <>
                  <div className="form-group">
                    <label>Владелец</label>
                    <select value={formData.ownerId} onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}>
                      <option value="">— Текущий пользователь —</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.phoneNumber})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Кредитный лимит (сом) <span style={{ color: '#6b7280', fontWeight: 400 }}>0 = без лимита</span></label>
                    <input type="number" min="0" step="0.01" value={formData.creditLimit}
                      onChange={(e) => setFormData({ ...formData, creditLimit: e.target.value })}
                      placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label>Макс. срок долга (дней) <span style={{ color: '#6b7280', fontWeight: 400 }}>0 = без ограничения</span></label>
                    <input type="number" min="0" step="1" value={formData.maxDebtDays}
                      onChange={(e) => setFormData({ ...formData, maxDebtDays: e.target.value })}
                      placeholder="например: 30" />
                  </div>
                </>
              )}
              <div className="form-group form-group-checkbox">
                <label>
                  <input type="checkbox" checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                  Активен
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>Отмена</button>
                <button type="submit" className="btn-primary">{editing ? 'Сохранить' : 'Создать'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Payment modal ── */}
      {paymentShop && (
        <div className="modal-overlay" onClick={() => setPaymentShop(null)}>
          <div className="modal modal-payments" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Расчёты — {paymentShop.name}</h2>
              <button className="modal-close" onClick={() => setPaymentShop(null)}>×</button>
            </div>
            <div className="payment-summary">
              <div className="payment-stat">
                <span>Долг</span>
                <strong style={{ color: debtColor(paymentShop) }}>{fmt(paymentShop.debt)} сом</strong>
              </div>
              <div className="payment-stat">
                <span>Аванс</span>
                <strong style={{ color: Number(paymentShop.advance) > 0 ? '#065f46' : '#6b7280' }}>
                  {fmt(paymentShop.advance)} сом
                </strong>
              </div>
              <div className="payment-stat">
                <span>Лимит</span>
                <strong>{Number(paymentShop.creditLimit) === 0 ? '∞ (без лимита)' : `${fmt(paymentShop.creditLimit)} сом`}</strong>
              </div>
              {Number(paymentShop.creditLimit) > 0 && (
                <div className="payment-stat">
                  <span>Свободно</span>
                  <strong style={{ color: '#065f46' }}>
                    {fmt(Number(paymentShop.creditLimit) - Number(paymentShop.debt))} сом
                  </strong>
                </div>
              )}
            </div>

            {isAdmin() && (
              <form onSubmit={handleAddPayment} className="payment-form">
                <h3>Записать оплату</h3>
                <div className="payment-form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Сумма (сом) *</label>
                    <input type="number" min="0.01" step="0.01" required value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Примечание</label>
                    <input type="text" value={payNotes}
                      onChange={(e) => setPayNotes(e.target.value)} placeholder="Наличные, перевод..." />
                  </div>
                  <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-end' }}>
                    Записать
                  </button>
                </div>
              </form>
            )}

            <div className="payment-history">
              <h3>История платежей</h3>
              {paymentsLoading ? (
                <p style={{ color: '#6b7280' }}>Загрузка...</p>
              ) : payments.length === 0 ? (
                <p style={{ color: '#6b7280' }}>Платежей нет</p>
              ) : (
              <div className="payment-table-wrap">
                <table className="payment-table">
                  <thead>
                    <tr><th>Дата</th><th>Сумма</th><th>Примечание</th><th>Кто записал</th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>{new Date(p.createdAt).toLocaleDateString('ru-RU')}</td>
                        <td style={{ color: '#065f46', fontWeight: 600 }}>+{fmt(p.amount)} сом</td>
                        <td>{p.notes || '—'}</td>
                        <td>{p.createdBy?.firstName} {p.createdBy?.lastName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Cards ── */}
      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : shops.length === 0 ? (
        <div className="empty-state"><p>Магазины не найдены</p></div>
      ) : (
        <>
          <div className="shops-grid">
            {shops.map((shop) => (
              <div key={shop.id} className={`shop-card ${!shop.isActive ? 'shop-inactive' : ''} ${shop.deletedAt ? 'shop-deleted' : ''}`}>
                <div className="shop-card-header">
                  <h3>{shop.name}</h3>
                  <span className={`status-badge ${shop.isActive ? 'active' : 'inactive'}`}>
                    {shop.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
                {shop.description && <p className="shop-description">{shop.description}</p>}

                {/* Balance block — only for admin and shop owner */}
                {!isAgent() && (
                  <div className="shop-balance-block">
                    <div className="balance-row">
                      <span>Долг:</span>
                      <strong style={{ color: debtColor(shop) }}>{fmt(shop.debt)} сом</strong>
                    </div>
                    <div className="balance-row">
                      <span>Аванс:</span>
                      <strong style={{ color: Number(shop.advance) > 0 ? '#065f46' : '#6b7280' }}>
                        {fmt(shop.advance)} сом
                      </strong>
                    </div>
                    <div className="balance-row">
                      <span>Лимит:</span>
                      <span>{Number(shop.creditLimit) === 0 ? '∞' : `${fmt(shop.creditLimit)} сом`}</span>
                    </div>
                    <div className="balance-row">
                      <span>Срок долга:</span>
                      <span>{Number(shop.maxDebtDays) === 0 ? '∞' : `${shop.maxDebtDays} дн.`}</span>
                    </div>
                    {Number(shop.creditLimit) > 0 && Number(shop.debt) > 0 && (
                      <div className="credit-bar">
                        <div
                          className="credit-fill"
                          style={{
                            width: `${Math.min(100, (Number(shop.debt) / Number(shop.creditLimit)) * 100)}%`,
                            backgroundColor: debtColor(shop),
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="shop-details">
                  {shop.address && (
                    <div className="shop-detail"><span className="detail-icon">📍</span><span>{shop.address}</span></div>
                  )}
                  {shop.phone && (
                    <div className="shop-detail"><span className="detail-icon">📞</span><span>{shop.phone}</span></div>
                  )}
                  {isAdmin() && shop.owner && (
                    <div className="shop-detail"><span className="detail-icon">👤</span><span>{shop.owner.firstName} {shop.owner.lastName}</span></div>
                  )}
                </div>

                <div className="shop-card-footer">
                  <span className="shop-date">{new Date(shop.createdAt).toLocaleDateString('ru-RU')}</span>
                  <div className="shop-actions">
                    {!shop.deletedAt && (
                      <button className="btn-detail" onClick={() => navigate(`/shops/${shop.id}`)}>Подробнее</button>
                    )}
                    {!isAgent() && !shop.deletedAt && (
                      <button className="btn-payments" onClick={() => openPayments(shop)}>💰 Расчёты</button>
                    )}
                    {!isAgent() && !shop.deletedAt && (
                      <button className="btn-edit" onClick={() => handleEdit(shop)}>Изменить</button>
                    )}
                    {isAdmin() && (
                      shop.deletedAt ? (
                        <button className="btn-restore" onClick={() => handleRestore(shop)}>Восстановить</button>
                      ) : (
                        <button className="btn-delete" onClick={() => handleDelete(shop)}>Удалить</button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="btn-page">← Назад</button>
              <span>Страница {page} из {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="btn-page">Вперёд →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Shops;
